
import React, { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import ChatSidebar from "./components/ChatSidebar";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/hooks/useAuth";
import { GripVertical, MessageCircle, LogIn } from "lucide-react";
import { useHaptics } from "@/hooks/useHaptics";
import { getTouchButtonClasses } from "@/utils/touchTargets";
import { cn } from '@flowstate/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { useChatContext } from "@/contexts/ChatContext";
import { agentBranding } from "./config/agentBranding";
import { logger } from "@/lib/logger";
import { useResizableSidebar } from "./hooks/useResizableSidebar";
import { CortexInitAnimation } from "./components/CortexInitAnimation";
import { CortexAISetupWizard, useCortexCredentialStatus } from "@flowstate/cortex-auth";
import { useAISettings } from "@/hooks/useAISettings";
import { useGoogleGeminiConnection } from "@/hooks/useGoogleGeminiConnection";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { AgentWindow } from "@/features/cortex/components/AgentWindow";

/** Feature flag — fullscreen drawer becomes the unified agent workbench. */
const CORTEX_WORKBENCH_ENABLED =
  (import.meta.env.VITE_CORTEX_WORKBENCH ?? "") === "true" ||
  (import.meta.env.VITE_CORTEX_WORKBENCH ?? "") === "1";

// Delay constants
const CHATBOT_TOAST_DELAY_MS = 3000;
const CHATBOT_TOAST_DURATION_MS = 8000;
const SIGNUP_TOAST_DELAY_MS = 15000;
const SIGNUP_TOAST_DURATION_MS = 10000;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// localStorage keys
const LS_KEY_CHATBOT_TOAST = 'hasSeenChatbotToast';
const LS_KEY_SIGNUP_TOAST_TIME = 'lastSignupToastTime';

const CORTEX_INIT_MIN_MS = 1500; // Show Cortex Framer Motion init for 1.5s when chat opens
/** Matches AdminShellFrame `h-14` navbar — AiAgent mounts outside that tree so use explicit offset. */
const CORTEX_SHELL_TOP = "3.5rem";

const AiAgent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const isMobile = useIsMobile();
  const isEffectiveFullscreen = isOpen && (isFullscreen || isMobile);
  const { isAuthenticated, session } = useAuth();
  const { light: hapticLight } = useHaptics();
  const { isChatOpen, setIsChatOpen } = useChatContext();
  const { settings, updateSettings } = useAISettings();
  const { isConnected: isGoogleGeminiConnected, connect: connectGoogleGemini } = useGoogleGeminiConnection();
  const { isReady: isCortexReady, isLoading: isCortexCredentialLoading } = useCortexCredentialStatus(
    supabase,
    settings,
  );

  // Resizable sidebar for main chat panel
  const { width: sidebarWidth, isResizing, handleResizeStart } = useResizableSidebar({
    sidebarId: 'chat-main',
    defaultWidth: 420,
    minWidth: 360,
    maxWidth: 900,
    enabled: isOpen && !isEffectiveFullscreen && !isMobile,
    invertDrag: true,
  });

  // Sync with external open requests (e.g. from InteractiveHeroSection)
  useEffect(() => {
    if (isChatOpen && !isOpen) {
      setIsOpen(true);
      setIsInitializing(true);
      if (isMobile) {
        setIsFullscreen(true);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("flowstate:cortex:open"));
        }
      }
    }
  }, [isChatOpen, isOpen, isMobile]);

  const handleOpenChat = useCallback(() => {
    logger.info("Opening chat sidebar");
    hapticLight();

    setIsOpen(true);
    setIsChatOpen(true);
    setIsInitializing(true);
    if (isMobile) {
      setIsFullscreen(true);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("flowstate:cortex:open"));
      }
    }
  }, [hapticLight, setIsChatOpen, isMobile]);

  // Keep fullscreen active when resized to mobile while chat is open
  useEffect(() => {
    if (isMobile && isOpen) {
      setIsFullscreen(true);
    }
  }, [isMobile, isOpen]);

  // Close Cortex when mobile navigation sidebar opens to prevent layering conflicts
  useEffect(() => {
    const handleMobileNavOpen = () => {
      if (isOpen) {
        logger.info("Closing chat because mobile navigation opened");
        setIsOpen(false);
        setIsChatOpen(false);
        setIsFullscreen(false);
      }
    };
    window.addEventListener("flowstate:sidebar:open-mobile", handleMobileNavOpen);
    return () => window.removeEventListener("flowstate:sidebar:open-mobile", handleMobileNavOpen);
  }, [isOpen, setIsChatOpen]);

  // Show init/thinking frame for at least CORTEX_INIT_MIN_MS every time chat opens
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => setIsInitializing(false), CORTEX_INIT_MIN_MS);
    return () => clearTimeout(t);
  }, [isOpen]);

  const handleCloseChat = useCallback(() => {
    logger.info("Closing chat sidebar");
    setIsOpen(false);
    setIsChatOpen(false);
    setIsFullscreen(false);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("flowstate:cortex:close"));
    }
  }, [setIsChatOpen]);

  const handleToggleFullscreen = useCallback(() => {
    logger.info("Toggling fullscreen mode");
    setIsFullscreen(prev => !prev);
  }, []);

  // Show toast notifications for first-time visitors
  useEffect(() => {
    let hasSeenChatbotToast = false;
    try {
      hasSeenChatbotToast = !!localStorage.getItem(LS_KEY_CHATBOT_TOAST);
    } catch {
      // localStorage unavailable – skip toast
      return undefined;
    }

    // Only show chatbot toast for first-time visitors after a short delay
    if (!hasSeenChatbotToast) {
      const chatbotToastTimer = setTimeout(() => {
        toast.success(agentBranding.introToast.title, {
          description: agentBranding.introToast.description,
          icon: <MessageCircle className="h-5 w-5 text-primary" />,
          action: {
            label: agentBranding.introToast.actionLabel,
            onClick: () => handleOpenChat(),
          },
          position: "bottom-right",
          duration: CHATBOT_TOAST_DURATION_MS,
        });
        try {
          localStorage.setItem(LS_KEY_CHATBOT_TOAST, 'true');
        } catch {
          // Quota exceeded or unavailable – ignore
        }
      }, CHATBOT_TOAST_DELAY_MS);
      
      return () => clearTimeout(chatbotToastTimer);
    }
    return;
  }, [handleOpenChat]);

  // Detect touch device to tweak UI behavior (tooltips, fullscreen default)
  useEffect(() => {
    let hasTouch = false;
    try {
      hasTouch =
        (typeof window !== "undefined" && "ontouchstart" in window) ||
        (typeof navigator !== "undefined" && (navigator.maxTouchPoints ?? 0) > 0);
    } catch (_e) {
      hasTouch = false;
    }
    setIsTouch(hasTouch);
  }, []);

  // Show sign-up toast for non-authenticated users
  useEffect(() => {
    // Only show for non-authenticated users
    if (!isAuthenticated) {
      let lastSignupToastTime: string | null = null;
      try {
        lastSignupToastTime = localStorage.getItem(LS_KEY_SIGNUP_TOAST_TIME);
      } catch {
        // localStorage unavailable – skip toast
        return undefined;
      }
      const currentTime = new Date().getTime();
      
      // Show toast if never shown before or shown more than 1 day ago
      if (!lastSignupToastTime || (currentTime - parseInt(lastSignupToastTime)) > ONE_DAY_MS) {
        const signupToastTimer = setTimeout(() => {
          toast.success("Join Our Community", {
            description: "Sign up for a free account to get full access to all features",
            icon: <LogIn className="h-5 w-5 text-green-500" />,
            action: {
              label: "Sign Up",
              onClick: () => {
                window.location.href = '/auth';
              },
            },
            closeButton: true,
            position: "top-center",
            duration: SIGNUP_TOAST_DURATION_MS,
          });
          try {
            localStorage.setItem(LS_KEY_SIGNUP_TOAST_TIME, currentTime.toString());
          } catch {
            // Quota exceeded or unavailable – ignore
          }
        }, SIGNUP_TOAST_DELAY_MS);
        
        return () => clearTimeout(signupToastTimer);
      }
    }
    return;
  }, [isAuthenticated]);

  return (
    <>
      {/* Chat bubble: compact, low-distraction button — reduces cognitive load for ADHD users */}
      {!isOpen && (
        isTouch ? (
          <Button
            id="chat-bubble-button"
            onClick={handleOpenChat}
            className="fixed rounded-full w-14 h-14 p-0 shadow-lg z-[100] overflow-hidden [contain:layout_paint] border-0 bg-transparent opacity-70 hover:opacity-100 focus:opacity-100 transition-opacity"
            style={{
              bottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
              right: "calc(env(safe-area-inset-right, 0px) + 24px)"
            }}
            aria-label={agentBranding.chatBubbleAriaLabel}
            data-testid="cortex-chat-bubble"
          >
            <CortexInitAnimation variant="bubble" className="w-full h-full" />
          </Button>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  id="chat-bubble-button"
                  onClick={handleOpenChat}
                  className={cn(
                    "fixed rounded-full w-12 h-12 p-0 shadow-md z-[100] overflow-hidden [contain:layout_paint] border-0 bg-transparent opacity-60 hover:opacity-100 focus:opacity-100 hover:scale-110 active:scale-95 transition-all duration-200",
                    getTouchButtonClasses('large', '')
                  )}
                  style={{
                    bottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                    right: "calc(env(safe-area-inset-right, 0px) + 24px)"
                  }}
                  aria-label={agentBranding.chatBubbleAriaLabel}
                  data-testid="cortex-chat-bubble"
                >
                  <CortexInitAnimation variant="bubble" className="w-full h-full" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p className="font-semibold">{agentBranding.chatBubbleTooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      )}

      {/* Chat sidebar — resizable, frosted glass, right-edge drawer */}
      <div
        data-cortex-drawer="right"
        data-open={isOpen}
        data-fullscreen={isEffectiveFullscreen}
        data-testid="cortex-sidebar"
        className={cn(
          "fixed transition-all ease-in-out overflow-hidden flex flex-col min-h-0 right-0",
          isResizing ? "duration-0" : "duration-300",
          isOpen ? "pointer-events-auto" : "pointer-events-none",
          isEffectiveFullscreen ? "z-[52]" : "z-30"
        )}
        style={
          isEffectiveFullscreen
            ? {
                top: "var(--navbar-height, 3.5rem)",
                right: 0,
                bottom: 0,
                width: "calc(100vw - var(--sidebar-width, 0px))",
                height: "calc(100dvh - var(--navbar-height, 3.5rem))",
              }
            : {
                top: CORTEX_SHELL_TOP,
                right: 0,
                width: isOpen ? sidebarWidth : 0,
                bottom: 0,
                height: `calc(100dvh - ${CORTEX_SHELL_TOP})`,
                minWidth: 0,
              }
        }
      >
        {/* Resize handle — left edge grip (mirrors console bottom-panel drag handle) */}
        {!isEffectiveFullscreen && isOpen && !isMobile && (
          <div
            className={cn(
              "absolute left-0 top-0 bottom-0 w-5 -translate-x-1/2 z-50",
              "flex items-center justify-center cursor-ew-resize",
              "text-muted-foreground/50 hover:text-muted-foreground transition-colors",
              "pointer-events-none",
              isResizing && "text-muted-foreground",
            )}
            aria-hidden
          >
            <div
              onMouseDown={handleResizeStart}
              onTouchStart={handleResizeStart}
              className="pointer-events-auto flex items-center justify-center h-8 w-4"
              aria-label="Resize Cortex panel"
              role="separator"
              aria-orientation="vertical"
            >
              <GripVertical className="h-4 w-4" />
            </div>
          </div>
        )}
          <div className="flex-1 min-h-0 h-full overflow-hidden">
            {isOpen && !isCortexReady ? (
              <div className="h-full overflow-y-auto rounded-2xl border border-border/20 bg-background/70 backdrop-blur-xl backdrop-saturate-150 shadow-2xl p-4">
                <CortexAISetupWizard
                  supabase={supabase}
                  accessToken={session?.access_token ?? ""}
                  supabaseUrl={import.meta.env.VITE_SUPABASE_URL ?? ""}
                  isGoogleConnected={isGoogleGeminiConnected}
                  onConnectGoogle={connectGoogleGemini}
                  allowOpenRouterPlatform={false}
                  settings={settings}
                  onSettingsChanged={updateSettings}
                  mode="inline"
                />
              </div>
            ) : CORTEX_WORKBENCH_ENABLED && isEffectiveFullscreen ? (
              <div className="h-full min-h-0 overflow-hidden rounded-2xl border border-border/20 bg-background/70 backdrop-blur-xl shadow-2xl">
                <AgentWindow
                  mode="workbench"
                  onClose={handleCloseChat}
                  onToggleFullscreen={handleToggleFullscreen}
                  isFullscreen
                />
              </div>
            ) : (
              <ChatSidebar
                isOpen={isOpen}
                onClose={handleCloseChat}
                onToggleFullscreen={handleToggleFullscreen}
                isFullscreen={isEffectiveFullscreen}
                isInitializing={isInitializing}
              />
            )}
          </div>
        </div>
    </>
  );
};

export default AiAgent;
